import * as React from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { UniversalImporterContractsPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = {
  employeesIds?: number[]
  selectedLegalEntity?: string
  contractMode?: 'all' | 'current'
} | null

const UniversalImporterContractsPage = () => {
  const {
    company,
    access: { locale, id: currentUser },
  } = useCurrentSession()

  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedIds = new Set(locationState?.employeesIds ?? [])
  const selectedLegalEntityId = locationState?.selectedLegalEntity
  const only_reference_contracts = locationState?.contractMode !== 'all'

  const nuvo_import_type = 'contract_version_importer'
  const { data, loading, error } = useQuery(UniversalImporterContractsPageDocument, {
    variables: { companyId: company.id, legalEntityId: Number(selectedLegalEntityId) },
    fetchPolicy: 'network-only',
    skip: !selectedLegalEntityId,
  })

  const canImportResources = data?.universalImporter._policies.importResources.can
  const canEditContracts = data?.contracts._policies.contractEditContractVersion.can

  const authorized = canImportResources && canEditContracts

  const legalEntityCountry = data?.companies.legalEntitiesConnection.nodes[0]?.country
  const legalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [Number(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const metadata = {
    legal_entity_ids: legalEntitiesIds,
    employee_ids: Array.from(selectedIds),
    legal_entity_countries: legalEntityCountry ? [legalEntityCountry] : [],
    only_reference_contracts: [only_reference_contracts],
  }

  const tracker_id = crypto.randomUUID()

  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser,
      company_id: company.id,
      legal_entity_ids: legalEntitiesIds,
      import_type: nuvo_import_type,
      tracker_id: tracker_id,
    }),
    [currentUser, company.id, legalEntitiesIds, nuvo_import_type, tracker_id]
  )

  if (loading) {
    return <Loading />
  }

  track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)

  return (
    <ResponsivePage.Body
      authorized={authorized}
      loading={loading}
      error={!selectedLegalEntityId || error}
      showHeader={false}
    >
      <NuvoImporter
        importType={nuvo_import_type}
        locale={locale}
        companyId={company.id}
        metadata={metadata}
        isEdition // Loads data from DB and shows hidden fields
        forceCreateAction // Sends isEdition=false to backend so it creates instead of updates
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default UniversalImporterContractsPage
